/**
 * Privacy Detection Utilities
 *
 * Utility functions to detect browser privacy features and blockers that may affect analytics.
 * These functions are designed to be non-intrusive and fail gracefully.
 */

/**
 * Tests if localStorage is available and functional.
 *
 * @returns false if localStorage works, true if blocked or unavailable
 *
 * @remarks
 * This method attempts to write a test value to localStorage and read it back.
 * If any exception occurs (QuotaExceededError, SecurityError, etc.),
 * localStorage is considered blocked.
 *
 * Common blocking scenarios:
 * - Private browsing mode (Safari, Firefox)
 * - Explicit storage blocking (browser settings)
 * - Security restrictions (iframes, cross-origin)
 */
export function isLocalStorageBlocked(): boolean {
  try {
    const testKey = "_anna_ls_test";
    localStorage.setItem(testKey, "test");
    localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    return false;
  } catch (e) {
    return true;
  }
}

/**
 * Tests if sessionStorage is available and functional.
 *
 * @returns false if sessionStorage works, true if blocked or unavailable
 *
 * @remarks
 * Similar to localStorage test but for sessionStorage.
 * SessionStorage may be blocked in private browsing or strict privacy modes.
 */
export function isSessionStorageBlocked(): boolean {
  try {
    const testKey = "_anna_ss_test";
    sessionStorage.setItem(testKey, "test");
    sessionStorage.getItem(testKey);
    sessionStorage.removeItem(testKey);
    return false;
  } catch (e) {
    return true;
  }
}

/**
 * Tests if first-party cookies are enabled and functional.
 *
 * @returns true if cookies are disabled or blocked, false if they work
 *
 * @remarks
 * Creates a test cookie, verifies it can be read back, and cleans it up.
 * First-party cookies may be blocked due to:
 * - Browser settings (block all cookies)
 * - Private browsing mode (some browsers)
 * - Browser extensions
 */
export function areCookiesDisabled(): boolean {
  try {
    const testKey = "_anna_cookie_test";
    const testValue = `test_${Date.now()}`;
    document.cookie = `${testKey}=${testValue}; path=/; SameSite=Lax`;
    const cookies = document.cookie;
    const cookieWorks = cookies.includes(testKey) && cookies.includes(testValue);
    // Clean up test cookie
    document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    return !cookieWorks;
  } catch (e) {
    return true;
  }
}

/**
 * Checks if the user has enabled Do Not Track (DNT).
 *
 * @returns true if DNT is enabled, false otherwise
 *
 * @remarks
 * Checks multiple DNT properties for cross-browser compatibility:
 * - navigator.doNotTrack
 * - window.doNotTrack
 * - navigator.msDoNotTrack (legacy IE)
 *
 * DNT values can be '1', 'yes', or other browser-specific values.
 */
export function isDNTEnabled(): boolean {
  return (
    navigator.doNotTrack === "1" ||
    (window as any).doNotTrack === "1" ||
    navigator.doNotTrack === "yes" ||
    (navigator as any).msDoNotTrack === "1"
  );
}

/**
 * Checks if third-party tracking scripts are blocked.
 *
 * @returns true if 2 or more third-party scripts are blocked, false otherwise
 *
 * @remarks
 * Checks for the presence of:
 * - Hotjar (window.hj)
 * - Google Tag Manager (window.dataLayer)
 * - Matomo Tag Manager (window._mtm)
 *
 * Conservative detection: Only flags as blocked if 2+ scripts are missing.
 * This reduces false positives when a single script fails to load for other reasons.
 */
export function areThirdPartyScriptsBlocked(): boolean {
  const hotjarBlocked = typeof (window as any).hj !== "function";
  const gtmBlocked = typeof (window as any).dataLayer === "undefined";
  const matomoBlocked = typeof (window as any)._mtm === "undefined";

  // Consider blocked if 2+ scripts failed to load
  const blockedCount = [hotjarBlocked, gtmBlocked, matomoBlocked].filter(Boolean).length;
  return blockedCount >= 2;
}

/**
 * Detects if an ad blocker or privacy blocker is active.
 *
 * @returns true if ad blocker detected, false otherwise
 *
 * @remarks
 * Uses two detection methods:
 *
 * 1. DOM Test: Creates an element with ad-like class names and checks if it's hidden.
 *    Ad blockers typically hide elements with classes like 'adsbox', 'ad-placement', etc.
 *
 * 2. Script Blocking Test: Checks if third-party scripts are blocked.
 *
 * Returns true if either test indicates blocking.
 *
 * Note: This is a best-effort detection. Sophisticated blockers may evade detection.
 */
export function isAdBlockerDetected(): boolean {
  // Test 1: DOM element test with ad-like class names
  const testElement = document.createElement("div");
  testElement.innerHTML = "&nbsp;";
  testElement.className = "adsbox ad-placement";
  testElement.style.position = "absolute";
  testElement.style.left = "-9999px";
  testElement.style.top = "-9999px";

  document.body.appendChild(testElement);

  const isHidden =
    testElement.offsetHeight === 0 ||
    testElement.offsetWidth === 0 ||
    window.getComputedStyle(testElement).display === "none" ||
    window.getComputedStyle(testElement).visibility === "hidden";

  // Clean up test element
  document.body.removeChild(testElement);

  // Test 2: Check if third-party scripts are blocked
  const scriptsBlocked = areThirdPartyScriptsBlocked();

  // Flag as ad blocker if DOM test fails OR scripts are blocked
  return isHidden || scriptsBlocked;
}
